import { useEffect, useMemo } from 'react';

import { commonTranslationKeys, createSingleInterpolatedTranslationKey, ValidationRules } from '@dreryk/react-common';
import { NumberInput } from '@dreryk/react-components';

import { medicalDevicesTranslationKeys } from '../../translations';
import {
   MedicalDeviceOrderFormMethods,
   MedicalDeviceOrderItemsCountFormBeanPart,
   MedicalDeviceOrderPlacementFormBeanPart,
} from '../medicalDeviceOrder.types';
import { isMedicalDeviceFieldRequired } from '../medicalDeviceOrder.utils';

type ItemsCountFieldProps = MedicalDeviceOrderFormMethods<
   MedicalDeviceOrderItemsCountFormBeanPart & MedicalDeviceOrderPlacementFormBeanPart,
   'register' | 'watch' | 'setValue' | 'formState' | 'trigger'
>;

const MIN_ITEMS_COUNT = 1;

const useValidationRules = (
   required: boolean,
   max: number
): ValidationRules<MedicalDeviceOrderItemsCountFormBeanPart & MedicalDeviceOrderPlacementFormBeanPart> => {
   return useMemo(
      () => ({
         required: required,
         validate: (data, getValue) => {
            const currentValue = (data as number | null) ?? 0;
            const placement = getValue('placement') as MedicalDeviceOrderPlacementFormBeanPart['placement'];
            const chosenPlacementsCount = placement?.length ?? 0;
            const min = MIN_ITEMS_COUNT * chosenPlacementsCount;
            if (!placement || placement?.length === 0) {
               return medicalDevicesTranslationKeys.fields.choosePlacementFirst;
            }
            if (currentValue < min) {
               return createSingleInterpolatedTranslationKey(commonTranslationKeys.validation.minLimit, 'limit', min);
            }
            if (currentValue > max) {
               return createSingleInterpolatedTranslationKey(commonTranslationKeys.validation.maxLimit, 'limit', max);
            }
            return undefined;
         },
      }),
      [required, max]
   );
};

const DEFAULT_VALUE = 0;

export const ItemsCountByPlacementField = (props: ItemsCountFieldProps): JSX.Element | null => {
   const device = props.watch('device');
   const placement = props.watch('placement');
   const chosenPlacementsCount = placement?.length ?? 0;

   const max = useMemo(() => {
      return (device?.limit ?? 0) * chosenPlacementsCount;
   }, [device, chosenPlacementsCount]);

   useEffect(() => {
      if (!props.formState.dirtyFields.itemsCount) {
         props.setValue('itemsCount', chosenPlacementsCount);
      }
      if (props.formState.dirtyFields.itemsCount) {
         props.trigger('itemsCount');
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [chosenPlacementsCount]);

   return (
      <NumberInput
         testId={'itemsCount'}
         controller={props.register(
            'itemsCount',
            useValidationRules(isMedicalDeviceFieldRequired(props.watch, 'itemsCount'), max)
         )}
         label={medicalDevicesTranslationKeys.fields.itemsCount}
         defaultValue={DEFAULT_VALUE}
      />
   );
};
